import { withTranslation } from "react-i18next";

import ISanitaryOverviewGraphProps from "./interfaces/ISanitaryOverviewGraphProps";

import BaseSanitaryOverviewGraph from "./baseSanitaryOverviewGraph";
import ChartDataCreator from "@/utils/chartDataCreator";
import Colors from "@/styles/colors";

class HourlySanitaryOverviewGraph extends BaseSanitaryOverviewGraph {
    private latestHour: number;

    private earliestHour: number;

    public constructor(props: ISanitaryOverviewGraphProps) {
        super(props);
    }

    public async updateChartData(): Promise<void> {
        if (this.props.venueId) {
            await this.setupVenueChartDataAsync();
        }
        else if (this.props.floorId) {
            await this.setupFloorChartDataAsync();
        }
        else {
            await this.setupAssetChartDataAsync();
        }
    }

    private async setupAssetChartDataAsync(): Promise<void> {
        const assetIds = this.props.assetIds || [];
        const ids = assetIds.map(a => a.value);
        const sanitaryData = await this.dataInsightsService.getHourlyAssetSanitaryData(ids, this.props.startDate, this.props.endDate);
        const [chartData, earliestHour, latestHour] = ChartDataCreator.createHourlySanitaryOverview(sanitaryData, assetIds, this.props.selectedDataType);

        this.earliestHour = earliestHour;
        this.latestHour = latestHour;

        if(assetIds.length === this.props.assetIds.length){
            this.setState({
                chartData: chartData
            });
        }
    }

    private async setupFloorChartDataAsync(): Promise<void> {
        if (!this.props.floorId) {
            return;
        }

        const floorId = this.props.floorId.value;

        const sanitaryData = await this.dataInsightsService.getHourlyFloorSanitaryData([floorId], this.props.startDate, this.props.endDate);

        // Avoids timing related null ref when floor became undefined while waiting for the request.
        if (!this.props.floorId) {
            return;
        }
        const [chartData, earliestHour, latestHour] = ChartDataCreator.createHourlySanitaryOverview(sanitaryData,
            [{
                label: this.props.floorId.label,
                value: this.props.floorId.value,
                color: Colors.royal_blue,
                textColor: Colors.royal_blue
            }],
            this.props.selectedDataType);

        // Avoids issue with long running requests overriding data if the user selects multiple options in quick succession.
        if (this.props.floorId.value === floorId) {
            this.earliestHour = earliestHour;
            this.latestHour = latestHour;
            this.setState({
                chartData: chartData
            });
        }
    }

    private async setupVenueChartDataAsync(): Promise<void> {
        if (!this.props.venueId) {
            return;
        }

        const venueId = this.props.venueId.value;

        const sanitaryData = await this.dataInsightsService.getHourlyVenueSanitaryData([venueId], this.props.startDate, this.props.endDate);

        // Avoids timing related null ref when venue became null while waiting for the request.
        if (!this.props.venueId) {
            return;
        }
        const [chartData, earliestHour, latestHour] = ChartDataCreator.createHourlySanitaryOverview(sanitaryData,
            [{
                label: this.props.venueId.label,
                value: this.props.venueId.value,
                color: Colors.royal_blue,
                textColor: Colors.royal_blue
            }],
            this.props.selectedDataType);

        if (this.props.venueId.value === venueId) {
            this.earliestHour = earliestHour;
            this.latestHour = latestHour;
            this.setState({
                chartData: chartData
            });
        }
    }

    public getLabels(): string[] {
        const hourNumbers = Array.from({ length: (this.latestHour - this.earliestHour) }, (v, k) => k + this.earliestHour);
        const hourLabels = hourNumbers.map((x) => `${x.toString()}:00`);

        return hourLabels;
    }
}

export default withTranslation()(HourlySanitaryOverviewGraph);